import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { addEvent, clearError } from '../redux/slices/eventsSlice';
import { toast } from 'react-toastify';
import { LoadingModal } from '../components/LoadingModal';
import { LocationAutocomplete } from './LocationAutocomplete';
import { validateEvent, sanitizeText } from '../utils/eventValidation';

const AddEventModal = ({ setAddEventModal }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const loading = useSelector((state) => state.events.loading);
  const error = useSelector((state) => state.events.error);

  const [name, setName] = useState('');
  const [subHeader, setSubHeader] = useState('');
  const [image, setImage] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [time, setTime] = useState('');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [signUpLink, setSignUpLink] = useState('');
  const [learnMoreLink, setLearnMoreLink] = useState('');
  const [translations, setTranslations] = useState({
    en: { name: '', subHeader: '', description: '' },
    es: { name: '', subHeader: '', description: '' },
  });
  const [validationErrors, setValidationErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [imageLoadError, setImageLoadError] = useState(false);

  useEffect(() => {
    // Clear any existing errors when component mounts
    dispatch(clearError());

    // Clean up when component unmounts
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const handleDismissError = () => {
    dispatch(clearError());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous validation errors
    setValidationErrors([]);
    setFieldErrors({});

    // Prepare event data
    const eventData = {
      name: sanitizeText(name),
      subHeader: sanitizeText(subHeader),
      image: image.trim(),
      startDate,
      endDate,
      time: sanitizeText(time),
      location: sanitizeText(location),
      description: sanitizeText(description),
      signUpLink: signUpLink.trim(),
      learnMoreLink: learnMoreLink.trim(),
      translations: {
        en: {
          name: sanitizeText(translations.en.name),
          subHeader: sanitizeText(translations.en.subHeader),
          description: sanitizeText(translations.en.description),
        },
        es: {
          name: sanitizeText(translations.es.name),
          subHeader: sanitizeText(translations.es.subHeader),
          description: sanitizeText(translations.es.description),
        },
      },
    };

    // Run comprehensive validation
    const validation = validateEvent(eventData);

    // Display warnings as toasts (non-blocking)
    if (validation.warnings && validation.warnings.length > 0) {
      validation.warnings.forEach((warning) => {
        toast.warning(warning, { autoClose: 5000 });
      });
    }

    // If validation fails, show errors and prevent submission
    if (!validation.isValid) {
      setValidationErrors(validation.errors);

      // Show first 3 errors as toasts
      validation.errors.slice(0, 3).forEach((error) => {
        toast.error(error);
      });

      if (validation.errors.length > 3) {
        toast.info(
          `${validation.errors.length - 3} more validation errors. Please check all fields.`
        );
      }

      // Scroll to top to show error summary
      const modalContent = document.querySelector('.overflow-y-auto');
      if (modalContent) {
        modalContent.scrollTop = 0;
      }

      return;
    }

    try {
      await dispatch(addEvent(eventData)).unwrap();

      // Clear form
      setName('');
      setSubHeader('');
      setImage('');
      setStartDate('');
      setEndDate('');
      setTime('');
      setLocation('');
      setDescription('');
      setSignUpLink('');
      setLearnMoreLink('');
      setTranslations({
        en: { name: '', subHeader: '', description: '' },
        es: { name: '', subHeader: '', description: '' },
      });
      setValidationErrors([]);
      setFieldErrors({});

      toast.success(t('eventManagement.success.eventAdded'));
      setAddEventModal(false);
    } catch (error) {
      console.error(t('eventManagement.errors.addFailed'), error);
      toast.error(error.message || t('eventManagement.errors.addFailed'));
    }
  };

  const canSubmit =
    name &&
    subHeader &&
    image &&
    startDate &&
    time &&
    location &&
    description &&
    translations.en.name &&
    translations.en.subHeader &&
    translations.en.description;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50">
      {(loading || error) && (
        <LoadingModal message="Adding event..." error={error} onClose={handleDismissError} />
      )}
      <div className="bg-white p-6 rounded-lg w-1/3 h-3/4 overflow-hidden flex flex-col">
        <h3 className="text-lg font-bold mb-4">Add New Event</h3>

        {/* Validation Error Summary */}
        {validationErrors.length > 0 && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <h4 className="text-sm font-semibold text-red-800 mb-2">
              Please fix the following errors:
            </h4>
            <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
              {validationErrors.slice(0, 5).map((error, index) => (
                <li key={index}>{error}</li>
              ))}
              {validationErrors.length > 5 && (
                <li className="text-red-600 font-semibold">
                  ...and {validationErrors.length - 5} more errors
                </li>
              )}
            </ul>
          </div>
        )}

        <div className="overflow-y-auto flex-grow pr-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name (English) */}
            <div>
              <label className="block font-semibold" htmlFor="translations-en-name">
                Name
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="text"
                id="translations-en-name"
                value={translations.en.name}
                onChange={(e) => {
                  setTranslations({
                    ...translations,
                    en: { ...translations.en, name: e.target.value },
                  });
                  setName(e.target.value);
                }}
                className="w-full p-2 border border-gray-300 rounded-md"
                maxLength="100"
                required
              />
              {translations.en.name.length > 75 && (
                <p
                  className={`text-xs mt-1 ${translations.en.name.length > 90 ? 'text-red-600' : 'text-yellow-600'}`}
                >
                  {translations.en.name.length}/100 characters
                </p>
              )}
            </div>

            {/* Name (Spanish) */}
            <div>
              <label className="block font-semibold" htmlFor="translations-es-name">
                Name (Spanish)
              </label>
              <input
                type="text"
                id="translations-es-name"
                value={translations.es.name}
                onChange={(e) =>
                  setTranslations({
                    ...translations,
                    es: { ...translations.es, name: e.target.value },
                  })
                }
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>

            {/* SubHeader (English) */}
            <div>
              <label className="block font-semibold" htmlFor="translations-en-subHeader">
                Sub-Header
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="text"
                id="translations-en-subHeader"
                value={translations.en.subHeader}
                onChange={(e) => {
                  setTranslations({
                    ...translations,
                    en: { ...translations.en, subHeader: e.target.value },
                  });
                  setSubHeader(e.target.value);
                }}
                className="w-full p-2 border border-gray-300 rounded-md"
                maxLength="300"
                required
              />
              {translations.en.subHeader.length > 250 && (
                <p
                  className={`text-xs mt-1 ${translations.en.subHeader.length > 280 ? 'text-red-600' : 'text-yellow-600'}`}
                >
                  {translations.en.subHeader.length}/300 characters
                </p>
              )}
            </div>

            {/* SubHeader (Spanish) */}
            <div>
              <label className="block font-semibold" htmlFor="translations-es-subHeader">
                Sub-Header (Spanish)
              </label>
              <input
                type="text"
                id="translations-es-subHeader"
                value={translations.es.subHeader}
                onChange={(e) =>
                  setTranslations({
                    ...translations,
                    es: { ...translations.es, subHeader: e.target.value },
                  })
                }
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>

            {/* Description (English) */}
            <div>
              <label className="block font-semibold" htmlFor="translations-en-description">
                Description
                <span className="text-red-500 ml-1">*</span>
              </label>
              <textarea
                id="translations-en-description"
                value={translations.en.description}
                onChange={(e) => {
                  setTranslations({
                    ...translations,
                    en: { ...translations.en, description: e.target.value },
                  });
                  setDescription(e.target.value);
                }}
                className="w-full p-2 border border-gray-300 rounded-md"
                rows="3"
                maxLength="500"
                placeholder="E.g. Join us for a community gathering to celebrate..."
                required
              />
              {translations.en.description.length > 400 && (
                <p
                  className={`text-xs mt-1 ${translations.en.description.length > 475 ? 'text-red-600' : 'text-yellow-600'}`}
                >
                  {translations.en.description.length}/500 characters
                </p>
              )}
            </div>

            {/* Description (Spanish) */}
            <div>
              <label className="block font-semibold" htmlFor="translations-es-description">
                Description (Spanish)
              </label>
              <textarea
                id="translations-es-description"
                value={translations.es.description}
                onChange={(e) =>
                  setTranslations({
                    ...translations,
                    es: { ...translations.es, description: e.target.value },
                  })
                }
                className="w-full p-2 border border-gray-300 rounded-md"
                rows="3"
              />
            </div>

            {/* Image URL */}
            <div>
              <label className="block font-semibold" htmlFor="image">
                Image URL
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="text"
                id="image"
                value={image}
                onChange={(e) => {
                  setImage(e.target.value);
                  setImageLoadError(false);
                }}
                className="w-full p-2 border border-gray-300 rounded-md"
                placeholder="https://example.com/image.jpg"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Must be a valid image URL (jpg, png, gif, webp, svg)
              </p>
              {image && !imageLoadError && (
                <div className="mt-2">
                  <img
                    src={image}
                    alt="Preview"
                    className="w-32 h-32 object-cover rounded-md"
                    onError={() => setImageLoadError(true)}
                  />
                </div>
              )}
              {imageLoadError && (
                <p className="text-xs text-red-600 mt-2">
                  ⚠ Unable to load image. Please check the URL.
                </p>
              )}
            </div>

            {/* Start Date */}
            <div>
              <label className="block font-semibold" htmlFor="startDate">
                Start Date
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="date"
                id="startDate"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
                required
              />
            </div>

            {/* End Date (Optional) */}
            <div>
              <label className="block font-semibold" htmlFor="endDate">
                End Date (Optional)
              </label>
              <input
                type="date"
                id="endDate"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
              />
              <p className="text-xs text-gray-500 mt-1">Leave blank for single-day events</p>
              {startDate && endDate && new Date(endDate) < new Date(startDate) && (
                <p className="text-xs text-red-600 mt-1">
                  ⚠ End date must be on or after start date
                </p>
              )}
            </div>

            {/* Time */}
            <div>
              <label className="block font-semibold" htmlFor="time">
                Time
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="text"
                id="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
                placeholder="ex: 9:00am-5:00pm"
                required
              />
            </div>

            {/* Location */}
            <div>
              <LocationAutocomplete
                id="location"
                name="location"
                label="Location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                required={true}
              />
            </div>

            {/* Signup Link */}
            <div>
              <label className="block font-semibold" htmlFor="signUpLink">
                Signup Link (Optional)
              </label>
              <input
                type="url"
                id="signUpLink"
                value={signUpLink}
                onChange={(e) => setSignUpLink(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
                placeholder="https://example.com/signup"
              />
              {signUpLink &&
                !signUpLink.startsWith('http://') &&
                !signUpLink.startsWith('https://') && (
                  <p className="text-xs text-red-600 mt-1">⚠ Must include http:// or https://</p>
                )}
            </div>

            {/* Learn More Link */}
            <div>
              <label className="block font-semibold" htmlFor="learnMoreLink">
                Learn More Link (Optional)
              </label>
              <input
                type="url"
                id="learnMoreLink"
                value={learnMoreLink}
                onChange={(e) => setLearnMoreLink(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
                placeholder="https://example.com/learn-more"
              />
              {learnMoreLink &&
                !learnMoreLink.startsWith('http://') &&
                !learnMoreLink.startsWith('https://') && (
                  <p className="text-xs text-red-600 mt-1">⚠ Must include http:// or https://</p>
                )}
            </div>
          </form>
        </div>
        <div className="flex justify-between mt-4">
          <button
            type="button"
            onClick={() => setAddEventModal(false)}
            className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded-md"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading || !canSubmit}
          >
            {loading ? 'Adding...' : 'Add Event'}
          </button>
        </div>
      </div>
    </div>
  );
};

export { AddEventModal };
